import React, { useMemo, useState, useEffect, useImperativeHandle } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  Typography,
  Button,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  saveSchedule,
  fetchUserSchedules,
  fetchControllerSchedules,
  saveAndActivateSchedule,
} from 'stores/sensor/controllersSlice';
import { getSchedule } from '../../../../components/Schedule/schedulesCache'; // sync action, fetchSchedule from controllerSync is async
import { useParams } from 'react-router-dom';
import CustomSnackbar from 'components/CustomSnackbar';
import { LOCK_LABELS, normalizeLockValue } from '../../../../constants/lockOptions';
import Loading from '../Loading';
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = Array.from({ length: 24 }, (_, i) => i); // 0..23

// Fixed hex palette for UI chrome (no theme/system colors)
const HEX = {
  white: '#FFFFFF',
  gridBorder: '#bcbcbcff',
  hoverOutline: '#585858ff',
  hourBg: '#F5F5F5',
  dayBg: '#FAFAFA',
  text: '#111111ff',
  subText: '#484848ff',
};

const DEFAULT_MODE = 'auto';
const DEFAULT_FAN_SPEED = 'auto';

const DEFAULT_ON_COLOR = '#33c83dff';

const HEX_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const isValidHex = value => typeof value === 'string' && HEX_REGEX.test(value);

const DEFAULT_PATTERN = {
  on_off: 0,
  device_type: null,
  smart_mode: false,
  comfort_mode: 'Neutral',
  channel_1: 0,
  channel_2: 0,
  mode: DEFAULT_MODE,
  fan_speed: DEFAULT_FAN_SPEED,
  temp_set_point: 25,
  lock: false,
  color: null,
  preset_id: null,
};

const normalizeOnOff = value => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower === 'on') return 1;
    if (lower === 'off') return 0;
  }
  if (value === true) return 1;
  if (value === false) return 0;
  return Number(value) === 1 ? 1 : 0;
};

const normalizeMode = value => {
  const key = String(value ?? '').trim().toLowerCase().replace(/[ -]/g, '_');
  return ['fan', 'cool', 'heat', 'auto'].includes(key) ? key : DEFAULT_MODE;
};

const normalizeFanSpeed = value => {
  const key = String(value ?? '').trim().toLowerCase();
  return ['auto', 'low', 'medium', 'high'].includes(key) ? key : DEFAULT_FAN_SPEED;
};

const normalizeTemp = value => {
  const num = Number(value);
  return Number.isFinite(num) ? num : DEFAULT_PATTERN.temp_set_point;
};

const resolveColor = (color, onOff) => {
  if (isValidHex(color)) return color;
  return onOff === 1 ? DEFAULT_ON_COLOR : null;
};

const getContrastTextColor = color => {
  if (!isValidHex(color)) return '#fff';
  const hex = color.replace('#', '');
  const fullHex =
    hex.length === 3
      ? hex
          .split('')
          .map(c => c + c)
          .join('')
      : hex;
  const r = parseInt(fullHex.slice(0, 2), 16);
  const g = parseInt(fullHex.slice(2, 4), 16);
  const b = parseInt(fullHex.slice(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 186 ? '#000' : '#fff';
};

const parseScheduleTimeToMinutes = value => {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const [hours, minutes = '0'] = value.split(':');
  const parsedHours = Number(hours);
  const parsedMinutes = Number(minutes);

  if (
    !Number.isInteger(parsedHours) ||
    !Number.isInteger(parsedMinutes) ||
    parsedHours < 0 ||
    parsedHours > 23 ||
    parsedMinutes < 0 ||
    parsedMinutes > 59
  ) {
    return null;
  }

  return parsedHours * 60 + parsedMinutes;
};

const isHourInsideCavRange = (dayIdx, hourIdx, cavStartTime, cavEndTime) => {
  const startMinutes = parseScheduleTimeToMinutes(cavStartTime?.[String(dayIdx)]);
  const endMinutes = parseScheduleTimeToMinutes(cavEndTime?.[String(dayIdx)]);

  if (startMinutes === null || endMinutes === null) return false;

  const hourStartMinutes = hourIdx * 60;
  if (endMinutes <= startMinutes) {
    return hourStartMinutes >= startMinutes || hourStartMinutes < endMinutes;
  }

  return hourStartMinutes >= startMinutes && hourStartMinutes < endMinutes;
};

const hydrateCell = data => {
  if (!data) return null;
  const on_off = normalizeOnOff(data.on_off ?? data.power);
  // const lock = normalizeLockValue(lock ?? data.lock);
  return {
    on_off,
    device_type: data.device_type ?? DEFAULT_PATTERN.device_type,
    mode: normalizeMode(data.mode),
    smart_mode: data.smart_mode ?? DEFAULT_PATTERN.smart_mode,
    comfort_mode: data.comfort_mode ?? DEFAULT_PATTERN.comfort_mode,
    channel_1: normalizeOnOff(data.channel_1),
    channel_2: normalizeOnOff(data.channel_2),
    fan_speed: normalizeFanSpeed(data.fan_speed ?? data.fan),
    temp_set_point: normalizeTemp(data.temp_set_point ?? data.temp),
    color: resolveColor(data.color, on_off),
    lock: data.lock,
    preset_id: data.preset_id ?? data.presetId ?? data.id ?? null,
  };
};

const getCellColorValue = cell => (typeof cell === 'string' ? cell : (cell?.color ?? null));

// 7x24 matrix of null | cell objects
const makeEmptyGrid = () => Array.from({ length: 7 }, () => Array(24).fill(null));

const coerceCell = cell => {
  if (cell && typeof cell === 'object') return cell;
  if (typeof cell === 'string') {
    return {
      on_off: DEFAULT_PATTERN.on_off,
      device_type: DEFAULT_PATTERN.device_type,
      smart_mode: DEFAULT_PATTERN.smart_mode,
      comfort_mode: DEFAULT_PATTERN.comfort_mode,
      channel_1: DEFAULT_PATTERN.channel_1,
      channel_2: DEFAULT_PATTERN.channel_2,
      mode: DEFAULT_PATTERN.mode,
      fan_speed: DEFAULT_PATTERN.fan_speed,
      temp_set_point: DEFAULT_PATTERN.temp_set_point,
      color: resolveColor(cell, DEFAULT_PATTERN.on_off),
      lock: DEFAULT_PATTERN.lock,
      preset_id: null,
    };
  }
  return { ...DEFAULT_PATTERN };
};

// Show the wand on smart-enabled pattern cells instead of the schedule button.
const isSmartPatternEnabled = value => {
  if (value === true || value === 1) return true;
  const smartMode = String(value ?? '').toLowerCase();
  return smartMode === 'true' || smartMode === 'solai' || smartMode === '1';
};

const cellHasSmartMode = cell => cell && typeof cell === 'object' && isSmartPatternEnabled(cell.smart_mode);

const SmartPatternIcon = ({ color }) => (
  <AutoFixHighIcon
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 20,
      height: 20,
      color,
      pointerEvents: 'none',
      filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.45))',
    }}
  />
);

const prepareInitialGrid = initialGrid => {
  if (!Array.isArray(initialGrid) || initialGrid.length !== 7) return makeEmptyGrid();
  return initialGrid.map(row => {
    if (!Array.isArray(row)) return Array(24).fill(null);
    return Array.from({ length: 24 }, (_, hourIdx) => {
      const cell = row[hourIdx];
      if (!cell) return null;
      if (typeof cell === 'object') return hydrateCell(cell);
      if (typeof cell === 'string') return hydrateCell({ color: cell });
      return null;
    });
  });
};

const patternsToGrid = (patterns = []) => {
  const next = makeEmptyGrid();

  for (const p of patterns) {
    // If your API is 0=Sun..6=Sat this is fine.
    // If it’s 1=Mon..7=Sun, uncomment the next line instead:
    // const d = ((Number(p.day_of_week) + 6) % 7); // shift so Mon(1) -> 1, Sun(7) -> 0
    const d = Number(p.day_of_week) ?? 0;
    const h = Number(p.hour_of_day) ?? 0;

    if (d >= 0 && d < 7 && h >= 0 && h < 24) {
      next[d][h] = hydrateCell(p, p.lock);
    }
  }
  return next;
};

// Optional: pass `initialGrid` and `onGridChange` from parent if you want external control
const ScheduleTable168 = React.forwardRef(function ScheduleTable168(
  {
    initialGrid,
    onGridChange,
    newScheduleMode,
    setNewScheduleMode,
    isRecommendationChanged,
    setIsRecommendationChanged,
    isRecommendationInEditMode,
    tempRecommendation,
    setIsRecommendationInEditMode,
  },
  ref,
) {
  const deviceId = useParams().id;
  const dispatch = useDispatch();
  const [scheduleName, setScheduleName] = useState('');
  const [recommendationName, setRecommendationName] = useState('');
  const selectedScheduleId = useSelector(s => s.controllers.selectedScheduleId);
  // const selectedScheduleId = useSelector(s => s.controllers.selectedScheduleId);
  const status = useSelector(s => s.controllers.schedulesStatus || 'idle');

  const selectedPreset = useSelector(s => s.controllers?.selectedSinglePreset);
  const presets = useSelector(s => s.controllers?.hourlyPresetsArray || []);
  const currentDeviceType = useSelector(s => s.controllers.currentMeasurements?.device_type);
  const cavStartTime = useSelector(s => s.controllers?.energyInfo?.cavStartTime);
  const cavEndTime = useSelector(s => s.controllers?.energyInfo?.cavEndTime);
  const isRecommendationSchedule = selectedScheduleId === 0;
  // const [isRecommendationSchedule, setisRecommendationSchedule] = useState(true);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [grid, setGrid] = useState(() =>
    initialGrid ? prepareInitialGrid(initialGrid) : makeEmptyGrid(),
  );

  const scheduleObjFromCache = useMemo(() => {
    // If we have a temp recommendation and selectedScheduleId is 0, use it
    if (selectedScheduleId === 0 && tempRecommendation) {
      return tempRecommendation.recommendation || tempRecommendation;
    }
    // Otherwise get from cache as before
    return getSchedule(selectedScheduleId);
  }, [selectedScheduleId, tempRecommendation, status]);

  const patterns = useMemo(() => {
    return grid.flatMap((row, dayIdx) =>
      row.map((cell, hourIdx) => {
        const base = coerceCell(cell);
        const on_off = base.on_off ?? DEFAULT_PATTERN.on_off;
        return {
          day_of_week: dayIdx, // 0..6 (Sun..Sat)
          hour_of_day: hourIdx, // 0..23
          on_off,
          device_type: base.device_type ?? currentDeviceType ?? DEFAULT_PATTERN.device_type,
          smart_mode: base.smart_mode ?? DEFAULT_PATTERN.smart_mode,
          comfort_mode: base.comfort_mode ?? DEFAULT_PATTERN.comfort_mode,
          channel_1: base.channel_1 ?? DEFAULT_PATTERN.channel_1,
          channel_2: base.channel_2 ?? DEFAULT_PATTERN.channel_2,
          mode: base.mode ?? DEFAULT_PATTERN.mode,
          fan_speed: base.fan_speed ?? DEFAULT_PATTERN.fan_speed,
          temp_set_point: base.temp_set_point ?? DEFAULT_PATTERN.temp_set_point,
          color: base.color ?? (on_off === 1 ? DEFAULT_ON_COLOR : null),
          lock: base.lock,
          preset_id: base.preset_id ?? null,
        };
      }),
    );
  }, [grid, currentDeviceType]);

  const presetNameById = useMemo(() => {
    const map = new Map();
    presets.forEach(p => {
      if (p && p.id != null && typeof p.name === 'string' && p.name.trim() !== '') {
        map.set(p.id, p.name);
      }
    });
    return map;
  }, [presets]);

  // find the pattern object for a specific (dayIdx, hourIdx)
  const getPatternForCell = (dayIdx, hourIdx) => {
    // patterns is an array of 168 objects like:
    // { day_of_week:0, hour_of_day:0, fan_speed:'high', mode:'auto', on_off:0, temp_set_point:25 }
    return patterns.find(p => p.day_of_week === dayIdx && p.hour_of_day === hourIdx);
  };

  const getTooltipTitle = (dayIdx, hourIdx) => {
    const p = getPatternForCell(dayIdx, hourIdx);

    // fallback in case nothing set yet
    if (!p) {
      return `${DAYS[dayIdx]} ${hourIdx}:00
    off`;
    }

    const onOffLabel = p.on_off === 1 ? 'ON' : 'OFF';
    const modeLabel = String(p.mode ?? '').toUpperCase();
    const fanLabel = String(p.fan_speed ?? '').toUpperCase();
    const lock = LOCK_LABELS[p.lock ?? 0] ?? (p.lock ? 'Locked' : 'Unlocked');
    const presetName = p.preset_id != null ? presetNameById.get(p.preset_id) : null;
    const isRelayPreset =
      p.device_type === 'Controller-relay-SEN54' || p.device_type === 'Controller_relay';

    const relayControl = String(
      p.smart_mode ?? p.smart_mode ?? DEFAULT_PATTERN.smart_mode,
    ).toLowerCase();
    const smartModeEnabled = relayControl === 'true' || relayControl === 'solai';
    const relayDetails = `${DAYS[dayIdx]} ${hourIdx}:00 - ${hourIdx + 1}:00.
    Smart mode: ${smartModeEnabled ? 'ON' : 'OFF'},
    Mode: ${modeLabel},
    Comfort Mode: ${p.comfort_mode ?? DEFAULT_PATTERN.comfort_mode}${
      relayControl === 'false' || relayControl === 'manual'
        ? `,
    Power: ${(p.on_off ?? DEFAULT_PATTERN.on_off) === 1 ? 'ON' : 'OFF'}`
        : ''
    }`.replace(/,\s*/g, ',\n');

    const details = isRelayPreset
      ? relayDetails
      : `${DAYS[dayIdx]} ${hourIdx}:00 - ${hourIdx + 1}:00.
    Smart mode: ${smartModeEnabled ? 'ON' : 'OFF'},${
      smartModeEnabled
        ? ''
        : `
    Power: ${onOffLabel},`
    }
    Temp: ${p.temp_set_point}°C,
    Mode: ${modeLabel},
    Fan: ${fanLabel},
    Lock: ${lock}`.replace(/,\s*/g, ',\n');

    return (
      <Box>
        {presetName ? (
          <Typography component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {presetName}
          </Typography>
        ) : null}
        <Typography component="div" sx={{ whiteSpace: 'pre-line', fontSize: '1rem' }}>
          {details}
        </Typography>
      </Box>
    );
  };

  // Grab the preset from Redux: state.controllers.selectedSinglePreset
  // Expected shape: { id, name, color: '#RRGGBB', ... }
  // Keep only valid hex colors. If invalid/missing, disable painting.
  const activePresetCell = useMemo(() => {
    if (!selectedPreset) return null;
    if (!isValidHex(selectedPreset.color)) return null;
    return hydrateCell({
      on_off: selectedPreset.power === 'on' ? 1 : 0,
      device_type: selectedPreset.device_type ?? DEFAULT_PATTERN.device_type,
      smart_mode: selectedPreset.smart_mode ?? DEFAULT_PATTERN.smart_mode,
      comfort_mode: selectedPreset.comfort_mode ?? DEFAULT_PATTERN.comfort_mode,
      channel_1: selectedPreset.channel_1 ?? DEFAULT_PATTERN.channel_1,
      channel_2: selectedPreset.channel_2 ?? DEFAULT_PATTERN.channel_2,
      mode: selectedPreset.mode,
      fan_speed: selectedPreset.fan,
      temp_set_point: selectedPreset.temp,
      lock: selectedPreset.lock,
      color: selectedPreset.color,
      preset_id: selectedPreset.id ?? null,
    });
  }, [selectedPreset]);

  const cellsMatch = (currentCell, presetCell) => {
    if (!currentCell || !presetCell) return false;
    const currentPresetId =
      typeof currentCell === 'object' && currentCell !== null
        ? (currentCell.preset_id ?? null)
        : null;
    if (currentPresetId && presetCell.preset_id) {
      return currentPresetId === presetCell.preset_id;
    }
    return getCellColorValue(currentCell) === getCellColorValue(presetCell);
  };

  const apply = (dayIdx, hourIdx) => {
    if (!isHourInsideCavRange(dayIdx, hourIdx, cavStartTime, cavEndTime)) return;
    // Editing is allowed only when creating a new schedule
    if (!newScheduleMode && !isRecommendationInEditMode) return;
    // No preset selected or invalid color → do nothing
    if (!activePresetCell) return;

    setGrid(prev => {
      const next = prev.map(row => row.slice());
      const existing = next[dayIdx][hourIdx];
      // toggle: same preset/color → clear, otherwise set data
      next[dayIdx][hourIdx] = cellsMatch(existing, activePresetCell)
        ? null
        : { ...activePresetCell };
      onGridChange?.(next);
      return next;
    });

    if (isRecommendationSchedule) {
      setIsRecommendationChanged(true);
    }
  };

  const canEditCells = (newScheduleMode || isRecommendationInEditMode) && activePresetCell;
  const isClearableCell = cell => canEditCells && cellsMatch(cell, activePresetCell);

  const cellSx = (cell, disabled) => {
    const color = getCellColorValue(cell);
    return {
      position: 'relative',
      minWidth: 40,
      minHeight: 10,
      maxHeight: '3px',
      p: 0,
      border: `1px solid ${HEX.gridBorder}`,
      // no 'transparent': when empty, just omit backgroundColor
      ...(disabled
        ? { backgroundColor: '#3a3a3a', opacity: 0.38 }
        : color
          ? { backgroundColor: color }
          : { backgroundColor: HEX.white }),
      '&:hover': disabled ? {} : { outline: `1px dashed ${HEX.hoverOutline}` },
      cursor: disabled
        ? 'not-allowed'
        : isClearableCell(cell)
          ? 'not-allowed'
          : canEditCells
            ? 'pointer'
            : 'default',
    };
  };

  const handleSaveSchedule = (triggerSource = 'table', payload) => {
    console.log(`handleSaveSchedule triggered by: ${triggerSource}`, payload || '');

    if (
      (triggerSource === 'schedule-picker' && payload.scheduleName === '') ||
      (triggerSource !== 'schedule-picker' && scheduleName === '')
    ) {
      if (scheduleName === '') {
        setSnack({
          open: true,
          message: 'Please name the new schedule',
          severity: 'error',
        });
        return;
      }
    }
    let scheduleData;

    if (triggerSource === 'schedule-picker') {
      scheduleData = {
        schedule_name: payload.scheduleName,
        device_ids: [Number(deviceId)],
      };
    } else {
      scheduleData = {
        schedule_name: scheduleName,
        device_ids: [Number(deviceId)],
      };
    }

    if (isRecommendationChanged === true) {
      scheduleData.is_auto_generated = false;
    }

    const deviceIds = [Number(deviceId)];

    return dispatch(saveAndActivateSchedule({ scheduleData, patterns, deviceIds }))
      .unwrap()
      .then(async result => {
        setSnack({
          open: true,
          message: 'Saved Schedule',
          severity: 'success',
        });
        setNewScheduleMode(false);

        setIsRecommendationInEditMode?.(false);
        dispatch(fetchControllerSchedules({ deviceId, includePatterns: true }));
        // return result;
      })
      .catch(err => {
        setSnack({
          open: true,
          message: err?.message || 'Error saving Schedule',
          severity: 'error',
        });
      });

    return dispatch(saveSchedule({ scheduleData, patterns, deviceIds }))
      .unwrap()
      .then(async result => {
        setSnack({
          open: true,
          message: 'Saved Schedule',
          severity: 'success',
        });
        setNewScheduleMode(false);
        let scheduleId = result.id;

        dispatch(activateScheduleForSingleDevice({ scheduleId, deviceId })).unwrap();

        setIsRecommendationInEditMode?.(false);
        // dispatch(fetchUserSchedules());
        dispatch(fetchControllerSchedules({ deviceId, includePatterns: true }));
        return result;
      })
      .catch(err => {
        setSnack({
          open: true,
          message: err || 'Error saving Schedule',
          severity: 'error',
        });
      });
  };

  useImperativeHandle(ref, () => ({
    saveSchedule: (trigger, payload) => handleSaveSchedule(trigger, payload),
    getPatterns: () => patterns,
  }));

  useEffect(() => {
    if (newScheduleMode) {
      setGrid(makeEmptyGrid());
      return;
    }

    // If no scheduleObjFromCache but we have tempRecommendation, use it directly
    const scheduleToUse =
      scheduleObjFromCache || (selectedScheduleId === 0 ? tempRecommendation : null);

    if (!scheduleToUse || !Array.isArray(scheduleToUse?.patterns)) return;

    const next = patternsToGrid(scheduleToUse.patterns);
    setGrid(next);
    onGridChange?.(next);

    if (selectedScheduleId === 0 && scheduleToUse.schedule_name) {
      setRecommendationName(scheduleToUse.schedule_name);
    }
  }, [newScheduleMode, scheduleObjFromCache, selectedScheduleId, tempRecommendation]);

  return (
    <Box>
      <CustomSnackbar
        {...snack}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        duration={3000}
        vertical="top"
        horizontal="left"
        color="#fff"
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', mb: 1 }}>
        <Tooltip
          title="Grey cells represent hours when the building is inactive and AC schedules cannot be modified."
          PopperProps={{
            sx: {
              zIndex: 50020,
            },
          }}
          slotProps={{
            popper: {
              sx: {
                zIndex: 50020,
              },
            },
          }}>
          <InfoOutlinedIcon sx={{ color: HEX.subText, fontSize: 22, cursor: 'help' }} />
        </Tooltip>
      </Box>
      <TableContainer component={Paper} sx={{ backgroundColor: HEX.white, borderRadius: '0' }}>
        <Table size="small">
          <TableBody>
            {DAYS.map((day, dIdx) => (
              <TableRow key={day}>
                {/* Day label on the LEFT */}
                <TableCell
                  sx={{
                    position: 'sticky', // keeps the label visible if container scrolls
                    left: 0,
                    zIndex: 1,
                    backgroundColor: HEX.dayBg,
                    border: `1px solid ${HEX.gridBorder}`,
                    width: 120,
                    fontWeight: 600,
                    color: HEX.text,
                    lineHeight: '.5',
                  }}>
                  {day}
                </TableCell>

                {/* 24 hour cells */}
                {HOURS.map(h => {
                  const cell = grid[dIdx][h];
                  const cellColor = getCellColorValue(cell);
                  const isDisabled = !isHourInsideCavRange(dIdx, h, cavStartTime, cavEndTime);
                  const tooltipBg = isValidHex(cellColor) ? cellColor : '#000';
                  const tooltipText = getContrastTextColor(tooltipBg);
                  const showSmartIcon = !isDisabled && cellHasSmartMode(cell);
                  return (
                    <Tooltip
                      key={h}
                      title={getTooltipTitle(dIdx, h)}
                      arrow
                      placement="top"
                      slotProps={{
                        popper: { sx: { zIndex: 50020 } },
                        tooltip: {
                          sx: {
                            backgroundColor: tooltipBg,
                            color: tooltipText,
                            boxShadow: '1px 3px 10px 0px rgba(0,0,0,0.75)',
                          },
                        },
                        arrow: { sx: { color: tooltipBg } },
                      }}>
                      <TableCell
                        onClick={() => apply(dIdx, h)}
                        sx={cellSx(cell, isDisabled)}>
                        {showSmartIcon ? <SmartPatternIcon color={tooltipText} /> : null}
                      </TableCell>
                    </Tooltip>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>

          {/* Hours row at the BOTTOM */}
          <TableFooter>
            <TableRow>
              {/* spacer cell under the day labels */}
              <TableCell
                sx={{
                  backgroundColor: HEX.hourBg,
                  borderTop: `1px solid ${HEX.gridBorder}`,
                  border: `1px solid ${HEX.gridBorder}`,
                  width: 120,
                  color: HEX.subText,
                  fontWeight: 600,
                  lineHeight: '1',
                }}>
                Hour
              </TableCell>
              {HOURS.map(h => (
                <TableCell
                  key={`h-${h}`}
                  align="left"
                  sx={{
                    backgroundColor: HEX.hourBg,
                    border: `1px solid ${HEX.gridBorder}`,
                    color: HEX.subText,
                    fontSize: 12,
                    fontWeight: '500',
                    padding: '2px 8px 2px 2px',
                  }}>
                  {h}:00
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      {newScheduleMode && !isRecommendationInEditMode && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <TextField
            id="outlined-basic"
            label="Schedule name"
            variant="outlined"
            placeholder="Type here…"
            value={scheduleName}
            onChange={e => setScheduleName(e.target.value)}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="save current patterns as a new schedule">
              <Button
                sx={{
                  height: '2rem',
                  backgroudColor: '#000',
                  color: '#fff',
                  borderRadius: '7px',
                  my: '1rem',
                  '&:hover': {
                    scale: '1',
                  },
                }}
                variant="contained"
                onClick={() => handleSaveSchedule('table-button')}>
                SAVE SCHEDULE
              </Button>
            </Tooltip>
            <Button
              sx={{
                height: '2rem',
                backgroudColor: '#000',
                color: '#ff0000',
                border: '1px solid #ff0000',
                borderRadius: '7px',
                my: '1rem',
                '&:hover': {
                  scale: '1',
                  color: '#fff',
                  backgroundColor: '#ff0000',
                },
              }}
              // variant="contained"
              onClick={() => setNewScheduleMode(false)}>
              CANCEL
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default ScheduleTable168;
